import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { BarChart3, Loader2, Download, Calendar } from 'lucide-react';
import { api } from '../../services/api';
import AdminLayout from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';

export default function AdminReports() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [recentScans, setRecentScans] = useState([]);
  const [recentPhotos, setRecentPhotos] = useState([]);

  const loadData = async () => {
    try {
      const [statsData, scansData, photosData] = await Promise.all([
        api.getDashboardStats(),
        api.getScans({ limit: 10 }),
        api.getPatrolPhotos({ limit: 10 }),
      ]);
      setStats(statsData);
      setRecentScans(scansData);
      setRecentPhotos(photosData);
    } catch (error) {
      console.error('Error loading reports:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-700" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-reports-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-slate-800">Analytics & Reports</h1>
          <div className="flex gap-2">
            <Button variant="outline" className="gap-2">
              <Download className="w-4 h-4" /> Export CSV
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Total Guards</p>
              <p className="text-3xl font-bold text-slate-800">{stats?.total_guards || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Total Sites</p>
              <p className="text-3xl font-bold text-slate-800">{stats?.total_sites || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Scans Today</p>
              <p className="text-3xl font-bold text-slate-800">{stats?.scans_today || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Open Incidents</p>
              <p className="text-3xl font-bold text-slate-800">{stats?.open_incidents || 0}</p>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Scans */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Recent Checkpoint Scans</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {recentScans.length === 0 ? (
                  <p className="text-slate-500 text-center py-4">No scans yet</p>
                ) : (
                  recentScans.map((scan) => (
                    <div key={scan.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium text-slate-800">{scan.checkpoint_name}</p>
                        <p className="text-sm text-slate-500">{scan.guard_name} • {scan.site_name}</p>
                      </div>
                      <span className="text-sm text-slate-500">
                        {format(new Date(scan.scanned_at), 'h:mm a')}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Recent Photos */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Recent Patrol Photos</CardTitle>
            </CardHeader>
            <CardContent>
              {recentPhotos.length === 0 ? (
                <p className="text-slate-500 text-center py-4">No photos yet</p>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {recentPhotos.slice(0, 6).map((photo) => (
                    <div key={photo.id} className="relative">
                      <img 
                        src={photo.photo} 
                        alt={photo.caption || 'Patrol photo'}
                        className="w-full h-24 object-cover rounded-lg"
                      />
                      <span className="absolute bottom-1 left-1 text-xs bg-black/50 text-white px-1 rounded">
                        {format(new Date(photo.timestamp), 'h:mm a')}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}